/**
 * The people of จู๋หมิง's 12 facilities (OW_1KM.md, `ZHUMING_FACILITIES` in data/regions/zhuming.ts): one villager
 * stands at each spot of the village, by day at the spot and at night at the door. Data only; `folk.ts` adds them to
 * `FOLK_LIST`. The looks are the existing NPC models (render3d/characters/npcs/table.ts); each dedicated trade model
 * comes later, a look is one field here. The mountain (no. 12) is a place, not a person.
 */
import type { FolkDef, Station } from './folk';

const at = (landmark: string, dx: number, dy: number, act: Station['act'], dur: readonly [number, number]): Station =>
  ({ at: landmark, dx, dy, act, dur }) as Station;

function post(
  id: string,
  name: string,
  title: string,
  sprite: string,
  home: string,
  spot: readonly [string, number, number],
  act: Station['act'],
  greet: readonly string[],
  work: readonly string[],
): FolkDef {
  const [lm, dx, dy] = spot;
  const day = [at(lm, dx, dy, act, [50, 90]), at(lm, dx + 1.5, dy + 1, 'stroll', [15, 25])];
  return {
    id: `zm-${id}`,
    name,
    title,
    sprite,
    zone: 'zhuming',
    home,
    speed: 1.2,
    schedule: { dawn: [at(lm, dx, dy, 'idle', [60, 90])], day, dusk: [at(lm, dx, dy, 'idle', [60, 90])], night: [{ at: home, act: 'home', dur: [600, 600] }] },
    lines: {
      greet,
      work: { [act]: work, idle: work },
      night: ['ดึกแล้ว พรุ่งนี้ค่อยมาคุยกัน'],
      scared: ['อย่ามาเกะกะแถวนี้!', 'ช่วยด้วย!'],
    },
  };
}

export const ZHUMING_FOLK: readonly FolkDef[] = [
  post('stable', 'ลุงหม่า', 'นายสถานีม้า', 'stationMaster', 'carriage', ['carriage', 0, -4], 'idle',
    ['เดินทางไกลก็ฝากม้าไว้ที่นี่ได้ {name}', 'ม้าตัวนี้วิ่งเร็วแต่ขี้เกียจกิน'], ['ตรวจเกือกม้าทุกเช้า…']),
  post('smith', 'ช่างเถียะ', 'ช่างตีเหล็ก', 'farmer', 'quarry', ['yardWallTop', 6.5, 6.5], 'call',
    ['ดาบทื่อก็เอามาลับ ดาบหักก็เอามาหลอม', 'เหล็กดีต้องรอไฟให้ได้ที่ {name}'], ['ก๊อง! ก๊อง! ตีเหล็กต้องตีตอนมันร้อน']),
  post('tailor', 'ป้าเฉิน', 'ช่างตัดผ้า', 'farmerAunt', 'shop', ['shop', 1.5, 1], 'call',
    ['ผ้าไหมใหม่เพิ่งมาถึง ลองดูไหม {name}', 'ชุดนักพรตตัดได้ในสามวัน'], ['เข็มเล่มนี้ใช้มาสามสิบปีแล้ว']),
  post('carpenter', 'ลุงหลู่', 'ช่างไม้', 'farmer', 'shore', ['shore', 0, 0], 'idle',
    ['ไม้ไผ่จากทะเลไผ่แข็งแรงที่สุด', 'ต้องการเรือหรือประตู ลุงทำให้ได้'], ['ขัดไม้ทีละเส้น ทีละเส้น…']),
  post('farmer', 'ลุงเหนียน', 'ชาวนา', 'farmer', 'fieldsWest', ['fieldsWest', 0, 0], 'hoe',
    ['ข้าวปีนี้งามนัก {name}', 'ฝนมาถูกเวลาแล้ว'], ['เหนื่อยแต่คุ้ม ข้าวไม่โกหกคน']),
  post('woodcutter', 'อาซาน', 'ช่างตัดฟืน', 'watchman', 'grove', ['grove', 0, 0], 'idle',
    ['ระวังหมาป่าตอนค่ำนะ {name}', 'ฟืนแห้งเก็บไว้หน้าหนาว'], ['ฉับ! ฉับ! ฟืนกองนี้ใกล้เต็มแล้ว']),
  post('beggar', 'ตาบอดหลี่', 'ขอทาน', 'fisherOld', 'gate', ['gate', -1.5, 0], 'sit',
    ['ใจบุญหน่อยเถอะ {name} ข้ารู้ข่าวลือหลายเรื่อง', 'ประตูนี้เห็นคนผ่านมาทุกชนิด'], ['ข่าวลือแลกข้าวได้ไหม…']),
  post('musician', 'หลี่เซียวเอ๋อ', 'นักดนตรี', 'scholar', 'pavilion', ['pavilion', 0, 0], 'sit',
    ['ฟังเพลงสักบทไหม {name}', 'เสียงพิณช่วยให้ลมปราณสงบ'], ['♪ …เสียงไผ่ขานรับสายลม…']),
  post('fishing', 'ลุงไป๋', 'ชาวประมง', 'fisherOld', 'home1', ['pierEnd', 0, 0], 'fish',
    ['ปลาตรงนี้กินเบ็ดดีนะ {name}', 'ยืมคันเบ็ดข้าได้'], ['เงียบหน่อย ปลาจะตกใจ']),
  post('inn', 'ป้าซู', 'เจ้าของโรงเตี๊ยม', 'innkeeper', 'inn', ['inn', -2.5, 0], 'call',
    ['เข้ามาพักก่อน {name} มีน้ำชาร้อนๆ', 'ห้องว่างเหลืออีกสองห้อง'], ['เชิญเลยจ้ะ เชิญเลย!']),
  post('yard', 'อาจารย์เหอ', 'ครูโรงฝึกยุทธ', 'guardOfficer', 'trainYard', ['trainYard', 1, 0], 'idle',
    ['ฝึกท่าพื้นฐานก่อนค่อยคิดเรื่องวิชาสูง {name}', 'โรงฝึกเปิดทุกเช้า'], ['ตั้งท่า หายใจลึก ๆ']),
];
